<template>
  <BaseLayout title="Create New Task">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <h1 class="display-4 text-center">Create New Task</h1>
      <div class="card">
        <div class="card-body">
          <SubtaskForm
            :subtask="subtask"
            buttonText="Create"
            @submitForm="createSubtask"
          />
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div class="card p-3">
        <h5 class="card-title">Quick Actions:</h5>
        <router-link :to="`/homework/${subtask.homework_id}`" class="btn btn-primary mb-2">
          <i class="bi bi-arrow-left"></i> Back to Homework
        </router-link>
      </div>
      <div class="card p-3 mt-3">
        <h5 class="card-title">Help:</h5>
        <button class="btn btn-primary mb-2" @click="openHelpInNewTab('evaluation')">
          <i class="bi bi-question-circle"></i> Open Help in New Tab for Evaluation
        </button>
        <button class="btn btn-info mb-2" @click="downloadTestSubtask">
          <i class="bi bi-eye"></i> Download Example Test Script (SQL)
        </button>
        <button class="btn btn-info mb-2" @click="downloadTestSubtaskPython">
          <i class="bi bi-eye"></i> Download Example Test Script (Python)
        </button>
        <button class="btn btn-primary mb-2" @click="openHelpInNewTab('pre_check')">
          <i class="bi bi-question-circle"></i> Open Help in New Tab for Pre-check
        </button>
        <button class="btn btn-info" @click="downloadPreCheckSubmission">
          <i class="bi bi-eye"></i> Download Example Pre-check Script
        </button>
      </div>
    </template>

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import SubtaskForm from '@/components/SubtaskForm.vue';
import SubtaskController from '@/controllers/SubtaskController';
import Subtask from '@/models/subtask';
import StemErrorModal from '@/components/StemErrorModal.vue';
import { mapGetters } from 'vuex';
// Set base dynamically, based on: if it is running on university server or not
const base = process.env.VUE_APP_UNI_SERVER === 'True' ? '/pa036-hw/' : '/';

export default {
  name: 'SubtaskCreateView',
  components: {
    BaseLayout,
    SubtaskForm,
    StemErrorModal,
  },
  computed: {
    ...mapGetters(['isTeacher']),
  },
  data() {
    return {      
      subtask: new Subtask({
        to_evaluate: true, // Most subtasks are meant to be evaluated
        homework_id: this.$route.params.homework_id,
      }),
    };
  },
  async created() {
    try {
      // Check if the user is a teacher
      if (!this.isTeacher) {
        this.$store.dispatch('triggerAlert', {
          type: 'error',
          title: 'Error',
          message: 'You are not authorized to access this page.',
        });
        this.$router.push(`/homeworks`);
        return;
      }
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    }
  },
  methods: {
    async createSubtask(subtaskData) {
      try {
        var new_subtask = await SubtaskController.createSubtask(subtaskData);

        // Upload new files
        await this.uploadFiles(new_subtask.id, new_subtask.homework_id, subtaskData);

        // Delete files
        await this.deleteFiles(new_subtask.id, subtaskData.filesToDelete);
        
        this.$store.dispatch('triggerAlert', {
          type: 'success',
          title: 'Task created',
          message: 'The Task was successfully created',
        });
        this.$router.push(`/subtask/${new_subtask.id}`);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async deleteSubtask(subtaskId) {
      if (confirm('Are you sure you want to delete this task?')) {
        try {
          await SubtaskController.deleteSubtask(subtaskId);

          this.$store.dispatch('triggerAlert', {
            type: 'success',
            title: 'Success',
            message: 'Task deleted successfully',
          });
          this.$router.push(`/homework/${this.subtask.homework_id}`);
        } catch (error) {
          this.$refs.stemErrorModal.show('Error', error.message);
        }
      }
    },
    async uploadFiles(subtaskId, homeworkId, updatedSubtask) {
      if (updatedSubtask.resource_files.length > 0) {
        const resourceFormData = new FormData();
        for (const file of updatedSubtask.resource_files) {
          resourceFormData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_resources', subtaskId, homeworkId, resourceFormData);
      }

      if (updatedSubtask.submission_files.length > 0) {
        const submissionFormData = new FormData();
        for (const file of updatedSubtask.submission_files) {
          submissionFormData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_submissions', subtaskId, homeworkId, submissionFormData);
      }

      if (updatedSubtask.test_files.length > 0) {
        const testFormData = new FormData();
        for (const file of updatedSubtask.test_files) {
          testFormData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_tests', subtaskId, homeworkId, testFormData);
      }
      
      if (updatedSubtask.precheck_files.length > 0) {
        const testPreCheckFormData = new FormData();
        for (const file of updatedSubtask.precheck_files) {
          testPreCheckFormData.append('files', file);
        }
        await SubtaskController.uploadFiles('save_subtask_pre_check_tests', subtaskId, homeworkId, testPreCheckFormData);
      }
    },
    // Download multiple files
    async downloadFiles(filePaths) {
      try {
        const response = await SubtaskController.downloadFiles(filePaths);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'subtask_files.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async deleteFiles(subtaskId, filesToDelete) {
      for (const filePath of filesToDelete.resource_file_paths) {
        await SubtaskController.deleteFile(subtaskId, filePath);
      }
      for (const filePath of filesToDelete.submission_file_paths) {
        await SubtaskController.deleteFile(subtaskId, filePath);
      }
      for (const filePath of filesToDelete.test_file_paths) {
        await SubtaskController.deleteFile(subtaskId, filePath);
      }
      for (const filePath of filesToDelete.test_pre_check_file_paths) {
        await SubtaskController.deleteFile(subtaskId, filePath);
      }
    },
    putListTogeter(list1, list2) {
      return list1.concat(list2);
    },
    openHelpInNewTab(targetUrl) {
      var urls = {
        'evaluation': '#worker_evaluation',
        'pre_check': '#pre_check',
      };
      
      // Open the help page in a new tab and scroll to the specific section
      window.open(base + 'help' + (urls[targetUrl] || ''), '_blank');
    },
    downloadTestSubtask() {
      window.open(base + 'assets/test_subtask.sh', '_blank');
    },
    downloadTestSubtaskPython() {
      window.open(base + 'assets/test_subtask.python-example.sh', '_blank');
    },
    downloadPreCheckSubmission() {
      window.open(base + 'assets/pre_check_submission.sh', '_blank');
    },
  },
};
</script>